import { Delete as DeleteIcon, Edit as EditIcon, MoreVert as MoreVertIcon, OpenInNew } from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo, useState } from 'react';

import { Pagination, PharmaceuticalCompanyListItem } from 'shared/api';

type Column = {
  id: string;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'abbreviatedName', label: 'Abbreviation', sortable: true, align: 'left' },
  { id: 'fullName', label: 'Full name', sortable: true, align: 'left' },
  { id: 'russianName', label: 'Russian name', sortable: true, align: 'left' },
  { id: 'country', label: 'Country', sortable: true, align: 'center' },
  { id: 'vaccineCountHuman', label: 'Human vaccines', sortable: false, align: 'center' },
  { id: 'vaccineCountPets', label: 'Pets vaccines', sortable: false, align: 'center' },
  { id: 'www', label: 'Website', sortable: false, align: 'left' },
  { id: 'control', label: 'Control', sortable: false, align: 'center' },
];

type Props = {
  list: PharmaceuticalCompanyListItem[];
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string) => void;
  onEdit: (company: PharmaceuticalCompanyListItem) => void;
  onDelete: (company: PharmaceuticalCompanyListItem) => void;
};

export const PharmaCompaniesTable = memo(
  ({
    list,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
    onDelete,
  }: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<PharmaceuticalCompanyListItem | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, company: PharmaceuticalCompanyListItem) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelected(company);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelected(null);
    };

    const handleEdit = () => {
      if (selected) onEdit(selected);
      handleMenuClose();
    };

    const handleDelete = () => {
      if (selected) onDelete(selected);
      handleMenuClose();
    };

    const handleChangePage = (_: unknown, newPage: number) => {
      onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="pharma companies table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => onSortChange(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length > 0 ? (
                  list.map((row) => (
                    <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="left">{row.abbreviatedName}</TableCell>
                      <TableCell align="left">{row.fullName}</TableCell>
                      <TableCell align="left">{row.russianName ?? '-'}</TableCell>
                      <TableCell align="center">{row.country}</TableCell>
                      <TableCell align="center">{row.vaccineCountHuman ?? 0}</TableCell>
                      <TableCell align="center">{row.vaccineCountPets ?? 0}</TableCell>
                      <TableCell align="left">
                        {row.www ? (
                          <Link
                            href={row.www}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            {row.www.replace(/^https?:\/\//, '')}
                            <OpenInNew fontSize="inherit" />
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No pharmaceutical companies found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

PharmaCompaniesTable.displayName = 'PharmaCompaniesTable';
